import React, { useState, useEffect } from "react";
import CryptoJS from "crypto-js";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  getDecryptedAccessToken,
  getDecryptedData,
  getDecryptedUserType,
} from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../../Utils/ApiServices/ApiServices";
import { useParams } from "react-router-dom";

const decryptionKey = process.env.REACT_APP_USER_ENC_DEC_KEY;
const BASE_URL = "http://18.117.9.252/";

const decryptText = (ciphertext) => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, decryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("Decryption Error:", error);
    return ciphertext; // Return original if decryption fails
  }
};

const UpdateLabReports = ({ }) => {
  const [alllabReports, setAllLabReports] = useState([]);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredReports, setFilteredReports] = useState([]);

  const accessToken = getDecryptedAccessToken();
  const LoginUserDetails = getDecryptedData();
  const LoginUserType = getDecryptedUserType();
  const params=useParams()
  const pid = LoginUserType === "Patient" ? LoginUserDetails?.pid : params.pid;

   useEffect(() => {
      if (!pid) return;
  
      const apiUrl =
        LoginUserType === "Patient"
          ? `/apis/default/portal/patient/${pid}/document`
          : `/apis/default/api/patient/${pid}/document`;
  
      ApiServices.getwitParams(apiUrl,
        { path: "lab_report" },
        { Authorization: `Bearer ${accessToken}` }
      )
        .then((response) => {
          if (response.data?.length) {
            setAllLabReports(response.data)}
        })
        .catch((error) => {
          console.error("Error fetching lab reports:", error);
          setAllLabReports([]);
        })
        .finally(() => setLoading(false));
    }, [pid, LoginUserType, accessToken]);


  useEffect(() => {
    if (alllabReports?.length) {
      const decryptedReports = alllabReports.map((report) => ({
        id: report.id,
        filename: decryptText(report.filename),
        docdate: report.docdate,
        url: BASE_URL + decryptText(report.url.split("/").pop()),
      }));
      setFilteredReports(decryptedReports);
    }
  }, [alllabReports]);

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (!query) {
      setFilteredReports(alllabReports);
      return;
    }

    const filtered = alllabReports.filter((report) =>
      decryptText(report.filename).toLowerCase().includes(query)
    );
    setFilteredReports(filtered);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = () => {
        setFile(reader.result);
      };
    }
  };

  const handleUpload = async () => {
    if (!file || !fileName.trim()) {
      setSnackbar({ open: true, message: "Both fields are required!", severity: "warning" });
      return;
    }

    setLoading(true);
    try {
      const response = await ApiServices.postwitParams(
        `/apis/default/portal/patient/${pid}/document`,
        { document: file, name: fileName.trim() },
        { path: "lab_report" },
        { Authorization: `Bearer ${accessToken}` }
      );

      if (response.data) {
        setSnackbar({ open: true, message: "Lab report uploaded successfully!", severity: "success" });
        setFile(null);
        setFileName("");
      }
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to upload lab report", severity: "error" });
      console.error("Upload Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
   

      <>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Enter the lab report name and select a file:
        </Typography>

        {/* Lab Report Name Input */}
        <TextField
          fullWidth
          label="Lab Report Name"
          variant="outlined"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
          sx={{ mb: 2 }}
          disabled={loading}
          required
        />

        <TextField
          fullWidth
          type="file"
          inputProps={{ accept: "application/pdf,image/*" }}
          onChange={handleFileChange}
          disabled={loading}
          required
        />

        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button
            onClick={handleUpload}
            variant="contained"
            sx={{ backgroundColor: "#028391", color: "white" }}
            disabled={loading || !file || !fileName.trim()}
          >
            {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Upload"}
          </Button>
        </Box>

        {/* Search Box */}
        <Box display="flex" alignItems="center" sx={{ mt: 4 }}>
          <SearchIcon sx={{ mr: 1, color: "#028391" }} />
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search by Lab Report Name"
            value={searchQuery}
            onChange={handleSearch}
          />
        </Box>

        {/* Lab Reports List */}
        {filteredReports.length > 0 ? (
          <Box sx={{ mt: 3 }}>
            {filteredReports.map((report) => (
              <Card key={report.id} sx={{ mb: 2, p: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {report.filename}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Date: {report.docdate}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    color="primary"
                    href={report.url}
                    target="_blank"
                  >
                    View Report
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <Typography sx={{ mt: 3, textAlign: "center", color: "gray" }}>
            No lab reports found.
          </Typography>
        )}

      {/* Snackbar for Notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </>
  );
};

export default UpdateLabReports;
// import React, { useState, useEffect } from "react";
// import CryptoJS from "crypto-js";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   TextField,
//   Button,
//   IconButton,
//   Box,
//   Typography,
//   CircularProgress,
//   Snackbar,
//   Alert,
//   Card,
//   CardContent,
//   CardActions,
// } from "@mui/material";
// import CloseIcon from "@mui/icons-material/Close";
// import SearchIcon from "@mui/icons-material/Search";
// import {
//   getDecryptedAccessToken,
//   getDecryptedData,
//   getDecryptedUserType,
// } from "../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
// import ApiServices from "../../../Utils/ApiServices/ApiServices";

// const decryptionKey = process.env.REACT_APP_USER_ENC_DEC_KEY;
// const BASE_URL = "http://18.117.9.252/";

// const decryptText = (ciphertext) => {
//   try {
//     const bytes = CryptoJS.AES.decrypt(ciphertext, decryptionKey);
//     return bytes.toString(CryptoJS.enc.Utf8);
//   } catch (error) {
//     console.error("Decryption Error:", error);
//     return ciphertext; // Return original if decryption fails
//   }
// };

// const UpdateLabReports = ({ open, handleClose, alllabReports }) => {
//   const [file, setFile] = useState(null);
//   const [fileName, setFileName] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
//   const [searchQuery, setSearchQuery] = useState("");
//   const [filteredReports, setFilteredReports] = useState([]);

//   const accessToken = getDecryptedAccessToken();
//   const LoginUserDetails = getDecryptedData();
//   const LoginUserType = getDecryptedUserType();
//   const pid = LoginUserType === "Patient" ? LoginUserDetails?.pid : null;

//   useEffect(() => {
//     if (alllabReports?.length) {
//       const decryptedReports = alllabReports.map((report) => ({
//         id: report.id,
//         filename: decryptText(report.filename),
//         docdate: report.docdate,
//         url: BASE_URL + decryptText(report.url.split("/").pop()),
//       }));
//       setFilteredReports(decryptedReports);
//     }
//   }, [alllabReports]);

//   const handleSearch = (e) => {
//     const query = e.target.value.toLowerCase();
//     setSearchQuery(query);

//     if (!query) {
//       setFilteredReports(alllabReports);
//       return;
//     }

//     const filtered = alllabReports.filter((report) =>
//       decryptText(report.filename).toLowerCase().includes(query)
//     );
//     setFilteredReports(filtered);
//   };

//   const handleFileChange = (e) => {
//     const selectedFile = e.target.files[0];
//     if (selectedFile) {
//       const reader = new FileReader();
//       reader.readAsDataURL(selectedFile);
//       reader.onload = () => {
//         setFile(reader.result);
//       };
//     }
//   };

//   const handleUpload = async () => {
//     if (!file || !fileName.trim()) {
//       setSnackbar({ open: true, message: "Both fields are required!", severity: "warning" });
//       return;
//     }

//     setLoading(true);
//     try {
//       const response = await ApiServices.postwitParams(
//         `/apis/default/portal/patient/${pid}/document`,
//         { document: file, name: fileName.trim() },
//         { path: "lab_report" },
//         { Authorization: `Bearer ${accessToken}` }
//       );

//       if (response.data) {
//         setSnackbar({ open: true, message: "Lab report uploaded successfully!", severity: "success" });
//         handleClose();
//         setFile(null);
//         setFileName("");
//       }
//     } catch (error) {
//       setSnackbar({ open: true, message: "Failed to upload lab report", severity: "error" });
//       console.error("Upload Error:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Dialog open={open} onClose={handleClose} fullScreen>
//       <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: 2, borderBottom: "1px solid #ddd" }}>
//         <DialogTitle sx={{ fontWeight: "bold", color: "#028391" }}>Upload Lab Report</DialogTitle>
//         <IconButton onClick={handleClose} disabled={loading}>
//           <CloseIcon />
//         </IconButton>
//       </Box>

//       <DialogContent sx={{ p: 3 }}>
//         <Typography variant="body1" sx={{ mb: 2 }}>
//           Enter the lab report name and select a file:
//         </Typography>

//         {/* Lab Report Name Input */}
//         <TextField
//           fullWidth
//           label="Lab Report Name"
//           variant="outlined"
//           value={fileName}
//           onChange={(e) => setFileName(e.target.value)}
//           sx={{ mb: 2 }}
//           disabled={loading}
//           required
//         />

//         <TextField
//           fullWidth
//           type="file"
//           inputProps={{ accept: "application/pdf,image/*" }}
//           onChange={handleFileChange}
//           disabled={loading}
//           required
//         />

//         <Box mt={3} display="flex" justifyContent="flex-end">
//           <Button onClick={handleClose} variant="outlined" sx={{ mr: 2 }} disabled={loading}>
//             Cancel
//           </Button>
//           <Button
//             onClick={handleUpload}
//             variant="contained"
//             sx={{ backgroundColor: "#028391", color: "white" }}
//             disabled={loading || !file || !fileName.trim()}
//           >
//             {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Upload"}
//           </Button>
//         </Box>

//         {/* Search Box */}
//         <Box display="flex" alignItems="center" sx={{ mt: 4 }}>
//           <SearchIcon sx={{ mr: 1, color: "#028391" }} />
//           <TextField
//             fullWidth
//             variant="outlined"
//             placeholder="Search by Lab Report Name"
//             value={searchQuery}
//             onChange={handleSearch}
//           />
//         </Box>

//         {/* Lab Reports List */}
//         {filteredReports.length > 0 ? (
//           <Box sx={{ mt: 3 }}>
//             {filteredReports.map((report) => (
//               <Card key={report.id} sx={{ mb: 2, p: 2 }}>
//                 <CardContent>
//                   <Typography variant="h6" sx={{ fontWeight: "bold" }}>
//                     {report.filename}
//                   </Typography>
//                   <Typography variant="body2" color="textSecondary">
//                     Date: {report.docdate}
//                   </Typography>
//                 </CardContent>
//                 <CardActions>
//                   <Button
//                     variant="contained"
//                     color="primary"
//                     href={report.url}
//                     target="_blank"
//                   >
//                     View Report
//                   </Button>
//                 </CardActions>
//               </Card>
//             ))}
//           </Box>
//         ) : (
//           <Typography sx={{ mt: 3, textAlign: "center", color: "gray" }}>
//             No lab reports found.
//           </Typography>
//         )}
//       </DialogContent>

//       {/* Snackbar for Notifications */}
//       <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
//         <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Dialog>
//   );
// };

// export default UpdateLabReports;
